import moment from 'moment';
import * as React from 'react';
import { TOOLTIP_DATETIME_FORMAT } from '../constants';
import ScaledCount from './ScaledCount';
import './Sparkline.scss';

const DEFAULT_HEIGHT = 48,
  DEFAULT_WIDTH = 260;

type Point = { x: number; y: number };

type SparkTooltipProps = {
  point: Point | null;
  xOffset?: number;
  valueSuffix: string;
};

class SparkTooltip extends React.Component<SparkTooltipProps> {
  render() {
    const { point, xOffset = 0, valueSuffix } = this.props;
    if (point) {
      const interval = moment(point.x).format(TOOLTIP_DATETIME_FORMAT);
      return (
        <div className="sparkline-tooltip" style={{ left: xOffset }}>
          <span>
            <ScaledCount count={point.y} decimals={1} showTooltip={false}>
              {valueSuffix}
            </ScaledCount>
          </span>
          <br />
          <span>{interval}</span>
        </div>
      );
    }
    return null;
  }
}

type Props = {
  data: Array<Point>;
  width?: number;
  height?: number;
  tooltipValueSuffix: string;
};

type State = {
  points: Array<Point> | null; // the input data converted/scaled to SVG coordinates
  line: string; // merged string of comma-separated pairs of the SVG path
  background: string; // merged string of path including bottom corner points for background polygon
  activePoint: Point | null; // the currently hovered data point for tooltip
  xOffset: number; // the pixel X-offset for the tooltip DOM element
  tooltipCursor: React.ReactNode | null; // the SVG element to render a line/cursor on the chart
};

export default class Sparkline extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.dataToPoints(props),
      activePoint: null,
      xOffset: 0,
      tooltipCursor: null,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.data !== prevProps.data) {
      this.setState({ ...this.dataToPoints(this.props) });
    }
  }

  /**
   * Converts the input data (x timestamp, y count) into SVG coordinates (x index, inverted y coordinate)
   * and provides coordinate strings for SVG polyline and closed polygon
   */
  dataToPoints = (props: Props) => {
    const { data, height = DEFAULT_HEIGHT, width = DEFAULT_WIDTH } = props;
    const yScale =
      height /
      data.reduce((runningMax, point) => Math.max(point.y, runningMax), 0);
    const xScale = width / (data.length - 1);
    const points = data.map(
      (point, index): Point => ({
        x: index * xScale,
        y: height - point.y * yScale,
      }),
    );
    if (points && points.length) {
      const line = points.map((point) => `${point.x},${point.y}`).join(' ');
      return {
        points,
        line,
        background:
          `-.01,${height} ` +
          line +
          ` ${points[points.length - 1].x + 0.01},${height}`,
      };
    } else {
      return {
        points: null,
        line: '',
        background: '',
      };
    }
  };

  /**
   * Shows the appropriate tooltip when SVG is hovered over by calculating the closest data point
   * to the cursor position (reverse mapping pixel coords to SVG coords), then setting the
   * 'active point' in state to that data point, as well as setting the appropriate pixel X-offset
   * for the tooltip and an SVG cursor element to render inside the SVG
   * @param event
   */
  showTooltip = (event: React.MouseEvent<SVGElement>) => {
    const { data, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = this.props;
    const { points } = this.state;
    if (!points || !points.length) {
      return;
    }
    const svgPixelBounds = event.currentTarget.getBoundingClientRect();
    const xPixel = Math.max(0, event.clientX - svgPixelBounds.left); // x-pixel coord within SVG
    const percentXOffset = xPixel / svgPixelBounds.width; // percentage left-to-right within SVG
    const index = Math.round((points.length - 1) * percentXOffset); // index of nearest data element
    const pointX = points[index].x; // SVG coord X offset of point
    const xPixelPointOffset = (pointX / width) * svgPixelBounds.width; // pixel coord X offset of point

    this.setState({
      activePoint: data[index],
      xOffset: xPixelPointOffset,
      tooltipCursor: (
        <line
          x1={pointX}
          x2={pointX}
          y1={0}
          y2={height}
          className="sparkline-cursor"
        />
      ),
    });
  };

  hideTooltip = () => {
    this.setState({ activePoint: null, tooltipCursor: null });
  };

  render() {
    const {
      tooltipValueSuffix,
      height = DEFAULT_HEIGHT,
      width = DEFAULT_WIDTH,
    } = this.props;
    const { line, background, xOffset, tooltipCursor } = this.state;
    return (
      <div className="position-relative">
        <SparkTooltip
          point={this.state.activePoint}
          xOffset={xOffset}
          valueSuffix={tooltipValueSuffix}
        />
        <svg
          className="sparkline"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <g onMouseMove={this.showTooltip} onMouseOut={this.hideTooltip}>
            <rect
              className="sparkline-backdrop"
              x={0}
              y={0}
              width={width}
              height={height}
            />
            <polygon className="sparkline-fill" points={background} />
            <polyline className="sparkline-line" points={line} />
            {tooltipCursor}
          </g>
        </svg>
      </div>
    );
  }
}
